// TEAMS Website - BookingSection Component
// Event booking consultation form

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../hooks/useScrollReveal.js';
import { convertAssetPath } from '../../utils/assetPath.js';
import Button from '../ui/Button.jsx';
import SectionHeader from '../ui/SectionHeader.jsx';
import '../../styles/main.scss';

const BookingSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });
  const currentLanguage = i18n.language;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(''); // 'success', 'error', ''

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Send form data to PHP email handler
      const response = await fetch(convertAssetPath('/send-email.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Success
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          message: ''
        });
      } else {
        // Error from server
        setSubmitStatus('error');
      }
    } catch (error) {
      // Network or other error
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get event type options based on current language
  const eventTypeOptions = t('booking.eventTypeOptions', { returnObjects: true });

  return (
    <section className="booking-section" ref={ref}>
      <div className="booking-section__container">
        <div className={`booking-section__content ${isVisible ? 'booking-section__content--visible' : ''}`}>
          {/* Section Header */}
          <SectionHeader
            title={t('booking.title')}
            subtitle={t('booking.subtitle')}
            alignment="center"
            size="large"
          />

          {/* Form and Info Container */}
          <div className="booking-section__form-and-info">
            {/* Booking Form */}
            <div className="booking-section__form-container">
            <form onSubmit={handleSubmit} className="booking-section__form">
              {/* First Row: Name, Email, Phone */}
              <div className="booking-section__form-grid booking-section__form-grid--3-cols">
                {/* Name Field */}
                <div className="booking-section__form-field">
                  <label htmlFor="name" className="booking-section__form-label">
                    {t('booking.form.name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="booking-section__form-input"
                    placeholder={t('booking.form.name')}
                  />
                </div>

                {/* Email Field */}
                <div className="booking-section__form-field">
                  <label htmlFor="email" className="booking-section__form-label">
                    {t('booking.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="booking-section__form-input"
                    placeholder={t('booking.form.email')}
                  />
                </div>

                {/* Phone Field */}
                <div className="booking-section__form-field">
                  <label htmlFor="phone" className="booking-section__form-label">
                    {t('booking.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="booking-section__form-input"
                    placeholder={t('booking.form.phone')}
                    pattern="[+]?[0-9]{1,4}?[-.\s]?[(]?[0-9]{1,3}?[)]?[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,9}"
                    title="Enter phone number with country code (e.g., +1 234 567 8900)"
                  />
                </div>
              </div>

              {/* Second Row: Event Type, Event Date */}
              <div className="booking-section__form-grid booking-section__form-grid--2-cols">
                {/* Event Type Field */}
                <div className="booking-section__form-field">
                  <label htmlFor="eventType" className="booking-section__form-label">
                    {t('booking.form.eventType')}
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    className="booking-section__form-select"
                  >
                    <option value="">
                      {t('booking.form.selectEventType')}
                    </option>
                    {eventTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Event Date Field */}
                <div className="booking-section__form-field">
                  <label htmlFor="eventDate" className="booking-section__form-label">
                    {t('booking.form.eventDate')}
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    className="booking-section__form-input"
                    min={new Date().toISOString().split('T')[0]} // Minimum date is today
                  />
                </div>
              </div>

              {/* Message Field */}
              <div className="booking-section__form-field booking-section__form-field--full">
                <label htmlFor="message" className="booking-section__form-label">
                  {t('booking.form.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  className="booking-section__form-textarea"
                  placeholder={t('booking.form.messagePlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <div className="booking-section__form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  className="booking-section__submit-button"
                >
                  {isSubmitting ? t('booking.form.submitting') : t('booking.form.submit')}
                </Button>
              </div>

              {/* Form Status Messages */}
              {submitStatus === 'success' && (
                <div className="booking-section__form-message booking-section__form-message--success">
                  {t('booking.form.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="booking-section__form-message booking-section__form-message--error">
                  {t('booking.form.error')}
                </div>
              )}
            </form>
          </div>

          {/* Additional Information */}
          <div className="booking-section__info">
            <div className="booking-section__info-item">
              <h4 className="booking-section__info-title">
                {t('booking.info.whyChoose.title')}
              </h4>
              <ul className="booking-section__info-list">
                <li>{t('booking.info.whyChoose.items.0')}</li>
                <li>{t('booking.info.whyChoose.items.1')}</li>
                <li>{t('booking.info.whyChoose.items.2')}</li>
                <li>{t('booking.info.whyChoose.items.3')}</li>
              </ul>
            </div>

            <div className="booking-section__info-item">
              <h4 className="booking-section__info-title">
                {t('booking.info.whatToExpect.title')}
              </h4>
              <ul className="booking-section__info-list">
                <li>{t('booking.info.whatToExpect.items.0')}</li>
                <li>{t('booking.info.whatToExpect.items.1')}</li>
                <li>{t('booking.info.whatToExpect.items.2')}</li>
                <li>{t('booking.info.whatToExpect.items.3')}</li>
              </ul>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
